// src/components/sections/CinematicCTA.tsx
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Container } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import { Beer } from 'lucide-react'
import { localizedRoutes } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

interface CinematicCTAProps {
  title?: string
  subtitle?: string
  ctaText?: string
  ctaLink?: string
  backgroundImage?: string
}

export function CinematicCTA({
  title,
  subtitle,
  ctaText,
  ctaLink,
  backgroundImage = '/images/cta-background.jpg',
}: CinematicCTAProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  return (
    <section className="relative py-16 md:py-24 overflow-hidden">
      {/* Background with parallax and gradient */}
      <motion.div
        initial={{ scale: 1.1 }}
        whileInView={{ scale: 1 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
        viewport={{ once: true }}
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      {/* Multi-layer overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-dark/80 via-brand-dark/60 to-brand-dark/80" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-transparent to-brand-dark/50" />

      {/* Animated accent line at top */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-primary to-transparent"
      />

      {/* Animated accent line at bottom */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-primary to-transparent"
      />

      {/* Decorative floating elements */}
      <motion.div
        animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-20 left-10 w-20 h-20 rounded-full bg-brand-primary/10 blur-2xl"
      />
      <motion.div
        animate={{ y: [0, 15, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-brand-accent/10 blur-3xl"
      />

      {/* Content */}
      <Container className="relative z-10 text-center">
        {/* Icon with animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, type: 'spring' }}
          viewport={{ once: true }}
          className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary shadow-lg shadow-brand-primary/30"
        >
          <Beer className="w-8 h-8 text-white" />
        </motion.div>

        {/* Title with staggered animation */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6"
        >
          <span className="bg-gradient-to-r from-white via-brand-primary to-white bg-clip-text text-transparent">
            {title || t('cta.title', 'Scopri i nostri birrifici')}
          </span>
        </motion.h2>

        {/* Decorative separator */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="w-24 h-1 mx-auto mb-6 bg-gradient-to-r from-transparent via-brand-primary to-transparent rounded-full"
        />

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed"
        >
          {subtitle || t('cta.subtitle', 'Birre artigianali selezionate da tutto il mondo per la tua attività')}
        </motion.p>

        {/* CTA Button with enhanced styling */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(ctaLink || `/${i18n.language}/${localizedRoutes.breweries[i18n.language as SupportedLanguage]}`)}
            className="group relative overflow-hidden bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary bg-[length:200%_100%] hover:bg-right transition-all duration-500 shadow-xl shadow-brand-primary/25 hover:shadow-2xl hover:shadow-brand-primary/40 hover:-translate-y-1 px-10 py-5 text-lg"
          >
            <span className="relative z-10 flex items-center gap-2">
              {ctaText || t('cta.button', 'Esplora i birrifici')}
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="group-hover:translate-x-1 transition-transform"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </motion.svg>
            </span>
          </Button>
        </motion.div>

      </Container>

    </section>
  )
}
